import { fetchScript } from "recipal-app/controllers/shared/requests";
import { overridesRecipePath, updateOverridesRecipePath } from "routes";

export const bindOverrides = () => {
  const containers = [
    document.querySelector('[data-partial="recipes/sidebar"]'),
    document.querySelector("#floating-preview-options"),
  ].filter(Boolean);

  const eventHandlers = {
    change: delegatedChangeHandler,
    click: delegatedClickHandler,
  };

  // Remove and add listeners back, to avoid potential double-binding
  containers.forEach((container) => {
    Object.entries(eventHandlers).forEach(([event, handler]) => {
      container.removeEventListener(event, handler);
      container.addEventListener(event, handler);
    });
  });
};

async function delegatedChangeHandler(event) {
  // Save individual nutrition override values when they change
  if (event.target.matches("input.override-value")) {
    const form = event.target.closest("#nutrition-override-form");
    if (form) {
      await updateOverrides(form);
    }
    return;
  }

  if (event.target.matches("#override-servings")) {
    const recipeId = event.target.dataset.recipe;
    if (!recipeId) return;

    const formData = new FormData();
    formData.append("recipe[override_servings]", event.target.value);

    await fetchScript(updateOverridesRecipePath(recipeId), {
      method: "post",
      body: formData,
    });

    try {
      // Note: this is needed because update_overrides.js.erb only re-renders sidebar_nutrition_overrides,
      // not the whole sidebar. So we must manually update the toggle here.
      // I tried unifying update_overrides.js.erb with show.js.erb (since they overlap), but it didn’t work out.
      // For now this works, but we could revisit DRYing up update_overrides.js.erb since it duplicates a lot from show.js.erb.
      const servings = parseFloat(event.target.value);
      const disableDualContainer = document.getElementById("disable-dual-column-container");

      // Element is only present on appropriate formats i.e. FDA vertical and tabular
      if (disableDualContainer) {
        if (servings >= 2 && servings <= 3) {
          disableDualContainer.style.display = "flex";
        } else {
          disableDualContainer.style.display = "none";
        }
      }
    } catch (err) {
      console.error(err);
    }

    return;
  }

  if (event.target.matches("#override-serving-size-weight-input")) {
    const recipeId = event.target.dataset.recipe;
    if (!recipeId) return;

    const currentValue = parseFloat(event.target.value);
    const initialValue = parseFloat(event.target.dataset.initialValue);
    const unitSelect = document.getElementById("override-serving-size-weight-unit");

    const formData = new FormData();
    // If either value is NaN, clear the override
    const valueToSend =
      Number.isNaN(currentValue) || Number.isNaN(initialValue)
        ? ""
        : Math.abs(currentValue - initialValue) < 0.01
          ? ""
          : event.target.value;
    formData.append("recipe[override_serving_size_weight]", valueToSend);
    if (unitSelect) {
      formData.append("recipe[package_yield_unit]", unitSelect.value);
    }

    await fetchScript(updateOverridesRecipePath(recipeId), {
      method: "post",
      body: formData,
    });
    return;
  }

  if (event.target.matches("#override-serving-size-weight-unit")) {
    const recipeId = event.target.dataset.recipe;
    if (!recipeId) return;

    const weightInput = document.getElementById("override-serving-size-weight-input");
    const unitValue = event.target.value;

    // Update the measurement type label
    const measurementType = document.getElementById("serving-size-measurement-type");
    if (measurementType) {
      measurementType.textContent = unitValue === "mL" ? "volume" : "weight";
    }

    const formData = new FormData();
    formData.append("recipe[package_yield_unit]", unitValue);
    if (weightInput && weightInput.value) {
      formData.append("recipe[override_serving_size_weight]", weightInput.value);
    }

    await fetchScript(updateOverridesRecipePath(recipeId), {
      method: "post",
      body: formData,
    });
    return;
  }

  // Aggregate/APAP labels: Load override data when selecting a recipe
  // This updates the sidebar's nutrition override fields to be populated with the selected recipe's values
  if (event.target.matches('#nutrition-override-form select[name="recipe_id"]')) {
    const form = event.target.closest("#nutrition-override-form");
    if (!form) return;

    const recipeId = event.target.value;
    const labelRecipeId = form.querySelector("[name=label_recipe_id]").value;
    await fetchScript(overridesRecipePath(recipeId, { label_recipe_id: labelRecipeId }));
  }
}

async function delegatedClickHandler(event) {
  if (event.target.matches("a.reset-all-overrides")) {
    event.preventDefault();

    const form = event.target.closest("#nutrition-override-form");
    if (!form) return;

    form.querySelectorAll("input.override-value").forEach((input) => {
      input.value = "";
    });

    await updateOverrides(form);
    return;
  }

  if (event.target.matches("a.reset-override") || event.target.closest("a.reset-override")) {
    event.preventDefault();

    const resetButton = event.target.matches("a.reset-override")
      ? event.target
      : event.target.closest("a.reset-override");

    const container = resetButton.closest("[data-nutrient-override]");
    if (!container) return;

    const input = container.querySelector("input");
    if (!input) return;

    input.value = "";

    const form = resetButton.closest("#nutrition-override-form");
    if (form) {
      await updateOverrides(form);
    }
  }
}

async function updateOverrides(form) {
  const recipeId = form.querySelector("[name=recipe_id]").value;
  await fetchScript(updateOverridesRecipePath(recipeId), {
    method: "post",
    headers: {
      Accept: "application/javascript",
    },
    body: new FormData(form),
  });
}

export function toggleOverrideInput(nutrient, display) {
  document.querySelectorAll(`div [data-nutrient-override="${nutrient}"]`).forEach((el) => {
    const hasOverride = el.dataset["hasOverride"] === "true";
    const shouldDisplay = hasOverride || display;
    if (shouldDisplay) {
      el.style.display = "block";
    } else {
      el.style.display = "none";
    }
  });
}
